import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Button, Select, Space, Tooltip, Spin, Switch } from 'antd';
import { CodeOutlined, DisconnectOutlined, CopyOutlined, SnippetsOutlined } from '@ant-design/icons';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { ClipboardAddon } from '@xterm/addon-clipboard';
import '@xterm/xterm/css/xterm.css';
import { TerminalAPI } from '../../apis/Terminal';
import { useDeploymentFeatures } from '../../containers/Dashboard/CustomHooks/useDeploymentFeatures';
import { AppContext } from '../../context/ContextProvider';
import './PodTerminal.css';

// Debug: Log xterm version
console.log('[terminal] Xterm version check:', {
  Terminal: typeof Terminal,
  FitAddon: typeof FitAddon,
  WebLinksAddon: typeof WebLinksAddon
});

const { Option } = Select;

/**
 * PodTerminal component for executing terminal commands on pods or kubectl sessions
 * Integrates with xterm.js for terminal emulation and WebSocket for real-time communication
 */
const PodTerminal = ({ 
  clusterUniqueId, 
  clusterName,
  pod,
  onClose,
  visible = false,
  className = '',
  terminalType = 'pod',  // 'pod' or 'kubectl'
  initialBanner = null,    // Optional read-only reference text printed
                           // to the xterm display once the session is
                           // ready. Does NOT go through the shell — it
                           // lives in xterm's scrollback so users can
                           // copy commands out of it. Used by the SRE
                           // Agent's "Run in terminal" entry point to
                           // surface LLM-suggested kubectl commands as
                           // a paste-able reference.
  autoConnect = false,     // When true, fires connectTerminal() once
                           // the xterm instance is ready and the
                           // session is idle. Used by entry points
                           // that know the user wants an active shell
                           // immediately (e.g. SRE Agent → kubectl).
                           // Resets on close so reopening triggers a
                           // fresh auto-connect.
}) => {
  // DEBUGGING: Log component render
  console.log('🔥 PodTerminal component rendered/re-rendered');
  
  // Get overviewData from context as fallback for cluster name
  const { overviewData } = useContext(AppContext);
  
  // Get cluster name from prop or fallback to overviewData
  const displayClusterName = clusterName || overviewData?.data?.display_name || 'Unknown';
  
  // State management
  const [terminal, setTerminal] = useState(null);
  const [websocket, setWebsocket] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState('');
  const [containers, setContainers] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [terminalHeight, setTerminalHeight] = useState(33); // Height in vh
  const [isResizing, setIsResizing] = useState(false);
  const [copyOnSelect, setCopyOnSelect] = useState(true);
  const { toggleAlert } = useDeploymentFeatures();
  // Removed reconnection logic as requested

  // Refs
  const terminalRef = useRef(null);           // DOM container for xterm
  const termInstanceRef = useRef(null);       // xterm instance
  const fitAddonRef = useRef(null);
  // Removed reconnection refs as requested
  const sessionIdRef = useRef(null); // Keep latest session id for event handlers
  const inputBufferRef = useRef(''); // Buffer typed characters until Enter
  const websocketRef = useRef(null); // Keep latest websocket for event handlers
  const isConnectedRef = useRef(false); // Keep latest connection status for event handlers
  const resizeStartYRef = useRef(0); // Starting Y position for resize
  const resizeStartHeightRef = useRef(33); // Starting height for resize
  const copyOnSelectRef = useRef(true); // For use inside terminal selection handler
  
  // Removed reconnection effect as requested

  // Keep sessionIdRef in sync
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Keep copyOnSelectRef in sync for selection handler
  useEffect(() => {
    copyOnSelectRef.current = copyOnSelect;
  }, [copyOnSelect]);

  // Initialize terminal when component mounts - SPLIT INTO TWO EFFECTS TO FIX DIMENSIONS ERROR
  // Step 1: Create terminal instance (without opening it yet)
  useEffect(() => {
    if (visible && pod && !terminal && !termInstanceRef.current) {
      console.log('[DEBUG] Creating terminal instance...');
      try {
        // Create terminal instance
      const term = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: '"Courier New", Courier, monospace',
        convertEol: true,
        lineHeight: 1.2,
          scrollback: 10000,
        theme: {
          background: '#000000',
            foreground: '#e0e0e0',
          cursor: '#ffffff',
          selection: '#ffffff40',
          black: '#000000',
            red: '#ff5555',
            green: '#50fa7b',  
            yellow: '#f1fa8c',
            blue: '#8be9fd',
            magenta: '#ff79c6',
            cyan: '#8be9fd',
            white: '#e0e0e0',
            brightBlack: '#6272a4',
            brightRed: '#ff6e6e',
            brightGreen: '#69ff94',
            brightYellow: '#ffffa5',
            brightBlue: '#d6acff',
            brightMagenta: '#ff92df',
            brightCyan: '#a4ffff',
            brightWhite: '#f0f0f0'
          }
        });

        // Create and attach addons
      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.loadAddon(new WebLinksAddon());
      term.loadAddon(new ClipboardAddon());

        // Store refs FIRST before setting state
        termInstanceRef.current = term;
        fitAddonRef.current = fitAddon;
        
        // Then set state which will trigger Step 2
        setTerminal(term);

        console.log('[DEBUG] Terminal instance created successfully');
      } catch (error) {
        console.error('[terminal] Error creating terminal instance:', error);
        setError('Failed to create terminal instance');
      }
      
      // Set containers
      if (terminalType === 'kubectl') {
        setContainers([{ name: 'kubectl' }]);
        setSelectedContainer('kubectl');
      } else {
        setContainers(pod.containers || []);
        if (pod.containers && pod.containers.length > 0) {
          setSelectedContainer(pod.containers[0].name || '');
        }
      }
    }
  }, [visible, pod, terminalType, terminal]);

  // Step 2: Open terminal in DOM after terminal instance is created
  useEffect(() => {
    if (terminal && terminalRef.current) {
      // Check if terminal is already opened
      const isAlreadyOpened = terminal.element !== undefined;
      
      if (!isAlreadyOpened) {
        console.log('[DEBUG] Opening terminal in DOM...');
        try {
          // Open terminal in the DOM element
          terminal.open(terminalRef.current);
          
          // Fit terminal to container size
          if (fitAddonRef.current) {
            setTimeout(() => {
              fitAddonRef.current?.fit();
            }, 0);
          }
          
          // Focus terminal (don't reset - it clears written content)
          terminal.focus();
          
          console.log('[terminal] Terminal opened successfully', {
            cols: terminal.cols,
            rows: terminal.rows,
            element: terminal.element
          });
          
          // Add click handler to maintain focus
          const clickHandler = () => {
            terminal.focus();
          };
          if (terminalRef.current) {
            terminalRef.current.addEventListener('click', clickHandler);
          }

          // Copy-on-select: when enabled, selection is copied to clipboard (ref synced with copyOnSelect state)
          const selectionChangeHandler = terminal.onSelectionChange(() => {
            if (!copyOnSelectRef.current) return;
            const sel = terminal.getSelection();
            if (sel && navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(sel).catch(() => {});
            }
          });
          terminalRef.current._selectionChangeHandler = selectionChangeHandler;

          // Add resize handler to ensure terminal fits properly
          const resizeHandler = () => {
            setTimeout(() => {
              if (fitAddonRef.current && terminalRef.current && terminal.element) {
                fitAddonRef.current.fit();
                console.log('[terminal] Terminal resized:', {
                  cols: terminal.cols,
                  rows: terminal.rows
                });
              }
            }, 100);
          };
          window.addEventListener('resize', resizeHandler);

          // Initial resize after DOM is fully settled
          setTimeout(() => {
            if (fitAddonRef.current && terminalRef.current) {
              fitAddonRef.current.fit();
            }
          }, 200);

          // Handle terminal input - send keystrokes to backend
          const dataHandler = terminal.onData((data) => {
            console.log(`[websocket] onData called with data: "${data}" (char code: ${data.charCodeAt(0)})`);
            console.log(`[websocket] onData state: isConnected=${isConnectedRef.current}, websocket=${!!websocketRef.current}, sessionId=${sessionIdRef.current}`);
            
            if (!isConnectedRef.current || !websocketRef.current) {
              console.log('[websocket] onData: ignoring keystroke - disconnected');
              return;
            }
            
            // Send every keystroke directly to backend - let TTY handle echo and line editing
            if (sessionIdRef.current && websocketRef.current) {
              console.log('[websocket] send: terminal_input (keystroke)');
              websocketRef.current.send(JSON.stringify({
                type: 'terminal_input',
                session_id: sessionIdRef.current,
                input: data
              }));
            }
          });

          // Cleanup function
          return () => {
            console.log('[DEBUG] Cleaning up terminal DOM bindings');
            dataHandler.dispose();
            if (terminalRef.current?._selectionChangeHandler) {
              terminalRef.current._selectionChangeHandler.dispose();
              terminalRef.current._selectionChangeHandler = null;
            }
            window.removeEventListener('resize', resizeHandler);
            if (terminalRef.current) {
              terminalRef.current.removeEventListener('click', clickHandler);
            }
          };
        } catch (error) {
          console.error('[terminal] Error opening terminal:', error);
          setError('Failed to open terminal');
        }
      } else {
        console.log('[DEBUG] Terminal already opened, skipping');
      }
    }
  }, [terminal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupTerminal();
    };
  }, []);
    
    // Resize terminal when modal becomes visible
  useEffect(() => {
    if (visible && termInstanceRef.current && fitAddonRef.current) {
      console.log('[DEBUG] Modal visible, resizing terminal');
      setTimeout(() => {
        fitAddonRef.current?.fit();
      }, 100);
    }
  }, [visible]);

  /**
   * Cleanup terminal and WebSocket connections
   */
  const cleanupTerminal = useCallback(() => {
    console.log('[DEBUG] cleanupTerminal called');
    
    // Send close command to backend before closing WebSocket
    if (websocket && sessionIdRef.current) {
      console.log('[DEBUG] Sending terminal close command to backend');
      try {
        websocket.send(JSON.stringify({
          type: 'terminal_close',
          session_id: sessionIdRef.current
        }));
      } catch (err) {
        console.log('[DEBUG] Error sending close command:', err);
      }
    }
    
    // Close WebSocket connection
    if (websocket) {
      console.log('[DEBUG] Closing WebSocket connection');
      websocket.close();
      setWebsocket(null);
      websocketRef.current = null; // Clear ref
    }
    
    // Dispose terminal instance
    if (termInstanceRef.current) {
      console.log('[DEBUG] Disposing terminal instance');
      termInstanceRef.current.dispose();
      termInstanceRef.current = null;
      setTerminal(null);
    }
    
    // Clean up resize handler
    if (terminalRef.current && terminalRef.current._resizeHandler) {
      window.removeEventListener('resize', terminalRef.current._resizeHandler);
      terminalRef.current._resizeHandler = null;
    }
    
    // Reset all state
    inputBufferRef.current = '';
    setIsConnected(false);
    isConnectedRef.current = false; // Clear ref
    setSessionId(null);
    sessionIdRef.current = null; // Clear ref
    setError(null);
    // Removed reconnection state resets
    
    console.log('[DEBUG] Terminal cleanup completed');
  }, [websocket]);

  /**
   * Create terminal session and establish WebSocket connection
   */
  const connectTerminal = useCallback(async () => {
    console.log('[DEBUG] connectTerminal called, pod:', !!pod, 'clusterUniqueId:', !!clusterUniqueId, 'selectedContainer:', !!selectedContainer);
    
    // Prevent multiple simultaneous connections
    if (isConnecting || isConnected) {
      console.log('[DEBUG] Already connecting or connected, ignoring duplicate call');
      return;
    }
    
    if (!pod || !clusterUniqueId || (!selectedContainer && terminalType !== 'kubectl')) {
      toggleAlert('error', 'Error', 'Missing required information for terminal connection');
      return;
    }

    // Check if kube-agent addon is installed
    // Skip validation for clusters imported via token that are private
    const checkImportFromToken = !overviewData?.data?.cluster_data?.cloud_account_id;
    const isImportedNoCloudCluster = overviewData?.data?.cluster_data?.provisioner === 'imported' && checkImportFromToken;
    
    if (!isImportedNoCloudCluster) {
      const addonsData = overviewData?.data?.addons_data;
      if (!addonsData || !addonsData['ops-agent']) {
        const errorMessage = 'ops-agent addon is missing. Please install it first to use the terminal.';
        setError(errorMessage);
        toggleAlert('error', 'Addon Required', errorMessage);
        return;
      }
    }

    console.log('[DEBUG] Setting isConnecting to true');
    setIsConnecting(true);
    setError(null);

    try {
      // Check permissions first
      const permissions = await TerminalAPI.checkPermissions(clusterUniqueId);
      if (!permissions.has_terminal_access) {
        setError('Access denied: You do not have permission to access terminal');
        return;
      }

      // Create terminal session - handle kubectl vs pod sessions
      const sessionData = terminalType === 'kubectl' 
        ? {
            session_type: 'kubectl',
            namespace: pod?.namespace || 'default',  // User's preferred namespace for kubectl context
            pod: 'kubectl-temp',                     // Will be generated by backend
            container: 'kubectl'
          }
        : {
            session_type: 'pod',
            namespace: pod.namespace,
            pod: pod.name,
            container: selectedContainer
          };
      let sessionResponse;
      try {
        sessionResponse = await TerminalAPI.createSession(clusterUniqueId, sessionData);
      } catch (err) {
        console.error('Error creating terminal session:', err);
        const message = err.response?.data?.details ?? err.response?.data?.error ?? err.message ?? 'Failed to create terminal session';
        toggleAlert('error', 'Error', message);
        setError(message);
        setIsConnecting(false);
        return;
      }
      // API may return 400 with { error, details, code } resolved as success by interceptor
      if (!sessionResponse?.session || sessionResponse?.error || sessionResponse?.code) {
        const message = sessionResponse?.details ?? sessionResponse?.error ?? 'Failed to create terminal session';
        toggleAlert('error', 'Error', message);
        setError(message);
        setIsConnecting(false);
        return;
      }
      const session = sessionResponse.session;
      setSessionId(session.session_id);
      sessionIdRef.current = session.session_id;

      // Get JWT token for authentication - use same token source as API client (isAuth)
      const token = localStorage.getItem('isAuth');
      
      console.log('[websocket] token present:', !!token, 'token length:', token?.length);
      
      if (!token || typeof token !== 'string' || token.trim() === '') {
        setError('Authentication token not found. Please login again.');
        setIsConnecting(false);
        return;
      }

      // Establish WebSocket connection with JWT token (query param)
      const apiUri = process.env.REACT_APP_API_URI || 'http://localhost:8000';
      const wsUri = apiUri.replace(/^http/, 'ws').replace(/^https/, 'wss');
      const wsUrl = `${wsUri}/ws/terminal/?session_id=${session.session_id}&auth_token=${encodeURIComponent(token)}`;
      
      console.log('[websocket] connecting:', wsUrl);
      
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[websocket] open');
        console.log('[DEBUG] WebSocket opened, setting states and refs');
        isConnectedRef.current = true; // Update ref for event handlers
        // Keep isConnecting true until we get actual connection confirmation
        // Removed reconnection state resets
        inputBufferRef.current = '';
        console.log('[DEBUG] About to write to terminal and focus, termInstanceRef.current:', !!termInstanceRef.current);
        
        // Clear terminal and reset cursor to top before showing connection messages
        termInstanceRef.current?.reset();
        
        // For kubectl terminals, wait for status updates before showing "connected"
        // For regular pod terminals, show connected immediately
        if (terminalType === 'kubectl') {
          termInstanceRef.current?.writeln('Initializing kubectl terminal...\r\n');
          // Don't set isConnected(true) yet - wait for "ready" status
        } else {
          // For pod terminals, also wait for actual connection confirmation
          termInstanceRef.current?.writeln('Connecting to pod terminal...');
        }
        
        termInstanceRef.current?.focus();
        console.log('[DEBUG] WebSocket onopen completed');
      };

      ws.onmessage = (event) => {
        // Try to parse structured messages first
        try {
          const data = JSON.parse(event.data);
          console.log('[websocket] message:', data?.type || 'unknown');
          if (data && data.type === 'terminal_output' && typeof data.output === 'string') {
            termInstanceRef.current?.write(data.output);
            return;
          }
          if (data && data.type === 'output' && typeof data.data === 'string') {
            termInstanceRef.current?.write(data.data);
            return;
          }
          if (data && data.type === 'terminal_connected') {
            // Connection confirmed for pod terminals
            if (terminalType !== 'kubectl') {
              termInstanceRef.current?.reset(); // Full reset including scrollback
              termInstanceRef.current?.writeln('\u001b[1;32mConnected to pod terminal\u001b[0m');
              termInstanceRef.current?.writeln(''); // Add empty line
              termInstanceRef.current?.write('# '); // Add prompt
              setIsConnected(true);
              setIsConnecting(false);
            }
            return;
          }
          if (data && data.type === 'terminal_status') {
            // Handle kubectl pod creation status updates - simplified
            const status = data.status;
            
            if (status === 'ready') {
              // Clear terminal, reset cursor to top, show connected message
              termInstanceRef.current?.reset(); // Full reset including scrollback
              termInstanceRef.current?.writeln('\u001b[1;32mConnected to kubectl terminal\u001b[0m');
              termInstanceRef.current?.writeln(''); // Add empty line
              // Optional reference banner (e.g. LLM-suggested commands from
              // the SRE Agent). Written via writeln so xterm renders the
              // line breaks correctly. Display-only — these chars are NOT
              // sent through the websocket, so the shell prompt below
              // stays empty and the user copy-pastes whichever command
              // they want to run.
              if (initialBanner && typeof initialBanner === 'string') {
                initialBanner.split('\n').forEach((line) => {
                  termInstanceRef.current?.writeln(line);
                });
                termInstanceRef.current?.writeln('');
              }
              termInstanceRef.current?.write('$ '); // Add prompt
              // Only now show "Connected" since terminal is actually ready
              setIsConnected(true);
              setIsConnecting(false);
            } else if (status === 'error') {
              termInstanceRef.current?.writeln(`\u001b[1;31mError: ${data.message}\u001b[0m`);
              setIsConnected(false);
              setIsConnecting(false);
            }
            // Ignore intermediate status updates (creating, starting, connecting)
            return;
          }
          if (data && data.type === 'error' && data.message) {
            termInstanceRef.current?.writeln(`\u001b[1;31mError: ${data.message}\u001b[0m`);
            return;
          }
        } catch (_) {
          // Not JSON, handle Blob or text data
          console.log('[websocket] message(non-json):', event.data);
          
          if (termInstanceRef.current && event.data) {
            // Handle Blob data
            if (event.data instanceof Blob) {
              console.log('[websocket] Received Blob, converting to text');
              event.data.text().then(text => {
                console.log('[websocket] Blob text content:', JSON.stringify(text));
                console.log('[websocket] Blob text hex:', Array.from(text).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join(' '));
                if (text && termInstanceRef.current) {
                  console.log('[websocket] Writing to terminal:', text.length, 'characters');
                  termInstanceRef.current.write(text);
                  console.log('[websocket] Terminal write completed');
                }
              }).catch(err => {
                console.error('[websocket] Error converting Blob to text:', err);
              });
            } else {
              // Handle regular text data
              console.log('[websocket] Received text data:', event.data);
              console.log('[websocket] Text data hex:', Array.from(event.data).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join(' '));
              if (termInstanceRef.current) {
                termInstanceRef.current.write(event.data);
              }
            }
          }
        }
      };

      ws.onerror = (error) => {
        console.error('[websocket] error:', error);
        setError('WebSocket connection error');
        setIsConnecting(false);
        setIsConnected(false);
      };

      // Handle close - no automatic reconnection
      ws.onclose = (event) => {
        console.log('[websocket] close:', event.code, event.reason);
        setIsConnected(false);
        isConnectedRef.current = false; // Update ref
        termInstanceRef.current?.writeln('\u001b[1;31m✗ Connection closed\u001b[0m');
        if (event.code !== 1000) {
          termInstanceRef.current?.writeln(`\u001b[1;31mCode: ${event.code}, Reason: ${event.reason || 'Unknown'}\u001b[0m`);
        }
        if (event.code === 4001) {
          setError('Authentication failed. Please login again.');
          toggleAlert('error', 'Authentication Failed', 'Please login again.');
          return;
        }
        // Show manual reconnect message
        if (event.code !== 1000) {
          setError('Connection lost. Click Connect to reconnect manually.');
          termInstanceRef.current?.writeln('\u001b[1;33m⚠ Click Connect button to reconnect manually\u001b[0m');
        }
      };

      setWebsocket(ws);
      websocketRef.current = ws; // Update ref for event handlers

    } catch (err) {
      console.error('Error connecting to terminal:', err);
      const message = err.response?.data?.details ?? err.response?.data?.error ?? err.message ?? 'Failed to connect to terminal';
      toggleAlert('error', 'Error', message);
      setError(message);
      setIsConnecting(false);
    }
  }, [pod, clusterUniqueId, selectedContainer, visible, overviewData, toggleAlert]);

  // Auto-connect on open when the caller opted in (e.g. SRE Agent "Run in
  // terminal"). Waits until the xterm instance is created and — for pod
  // mode — a container has been selected, then fires connectTerminal()
  // exactly once per open. The ref-based one-shot guard prevents a
  // ping-pong when the user manually disconnects; the ref clears when
  // `visible` flips to false so the next open auto-connects again.
  const hasAutoConnectedRef = useRef(false);
  useEffect(() => {
    if (!visible) {
      hasAutoConnectedRef.current = false;
      return;
    }
    if (!autoConnect) return;
    if (hasAutoConnectedRef.current) return;
    if (!terminal) return;
    if (isConnected || isConnecting) return;
    if (terminalType !== 'kubectl' && !selectedContainer) return;
    hasAutoConnectedRef.current = true;
    connectTerminal();
  }, [
    visible,
    autoConnect,
    terminal,
    terminalType,
    selectedContainer,
    isConnected,
    isConnecting,
    connectTerminal,
  ]);

  /**
   * Disconnect terminal session
   */
  const disconnectTerminal = useCallback(async () => {
    if (sessionId && clusterUniqueId) {
      try {
        await TerminalAPI.closeSession(clusterUniqueId, sessionId);
      } catch (err) {
        console.error('Error closing session:', err);
      }
    }
    
    cleanupTerminal();
    toggleAlert('success', 'Success', 'Terminal disconnected');
  }, [sessionId, clusterUniqueId, cleanupTerminal]);

  /**
   * Handle modal close - ensure proper cleanup
   */
  const handleClose = useCallback(() => {
    console.log('[DEBUG] Modal closing - cleaning up terminal connections');
    cleanupTerminal(); // Use cleanupTerminal instead of disconnectTerminal for thorough cleanup
    onClose?.();
  }, [cleanupTerminal, onClose]);

  /**
   * Handle container selection change
   */
  const handleContainerChange = useCallback((value) => {
    setSelectedContainer(value);
  }, []);

  /**
   * Handle resize start
   */
  const handleResizeStart = useCallback((e) => {
    console.log('[RESIZE] Resize start triggered', e);
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartYRef.current = e.clientY;
    resizeStartHeightRef.current = terminalHeight;
    
    console.log('[RESIZE] Starting values:', {
      clientY: e.clientY,
      terminalHeight,
      resizeStartY: resizeStartYRef.current,
      resizeStartHeight: resizeStartHeightRef.current
    });
    
    // Add global event listeners with passive: false for better performance
    document.addEventListener('mousemove', handleResizeMove, { passive: false });
    document.addEventListener('mouseup', handleResizeEnd, { passive: false });
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [terminalHeight]);

  /**
   * Handle resize move - using requestAnimationFrame for smooth updates
   */
  const handleResizeMove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Use requestAnimationFrame for smooth, synchronized updates
    requestAnimationFrame(() => {
      const viewportHeight = window.innerHeight;
      const deltaY = resizeStartYRef.current - e.clientY; // Inverted because we're resizing from bottom
      // Convert pixel delta to viewport height units
      const deltaVh = (deltaY / viewportHeight) * 100;
      const newHeight = Math.max(10, Math.min(80, resizeStartHeightRef.current + deltaVh)); // Min 10vh, Max 80vh
      
      console.log('[RESIZE] Move values:', {
        deltaY,
        deltaVh,
        newHeight,
        startY: resizeStartYRef.current,
        currentY: e.clientY,
        viewportHeight
      });
      
      setTerminalHeight(newHeight);
    });
  }, []);

  /**
   * Handle resize end
   */
  const handleResizeEnd = useCallback(() => {
    console.log('[RESIZE] Resize end triggered');
    setIsResizing(false);
    
    // Remove global event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    
    // Resize terminal after height change
    requestAnimationFrame(() => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    });
  }, []);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // Common commands based on terminal type
  const commonCommands = terminalType === 'kubectl' 
    ? [
        { label: 'get pods', command: 'kubectl get pods\n' },
        { label: 'get nodes', command: 'kubectl get nodes\n' },
        { label: 'get services', command: 'kubectl get services\n' },
        { label: 'get deployments', command: 'kubectl get deployments\n' },
        { label: 'cluster-info', command: 'kubectl cluster-info\n' }
      ]
    : [
        { label: 'env', command: 'env\n' },
        { label: 'top', command: 'top\n' },
        { label: 'curl', command: 'curl -I http://localhost:8080\n' },
        { label: 'kubectl describe pod', command: `kubectl describe pod ${pod?.name || ''}\n` }
      ];

  /**
   * Execute common command
   */
  const executeCommand = useCallback((command) => {
    const line = command.endsWith('\n') ? command.slice(0, -1) : command;
    inputBufferRef.current = '';
    // Don't echo locally - let TTY handle it to avoid double-echo
    if (websocketRef.current && isConnectedRef.current && sessionIdRef.current) {
      console.log('[websocket] send: terminal_input (quick command)');
      websocketRef.current.send(JSON.stringify({
        type: 'terminal_input',
        session_id: sessionIdRef.current,
        input: line + '\r'
      }));
    }
    termInstanceRef.current?.focus();
  }, []);

  /**
   * Copy current terminal selection to clipboard
   */
  const handleCopy = useCallback(() => {
    const sel = termInstanceRef.current?.getSelection?.();
    if (sel && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(sel).then(() => {
        toggleAlert('success', 'Copied', 'Selection copied to clipboard');
      }).catch(() => {
        toggleAlert('error', 'Copy failed', 'Could not copy to clipboard');
      });
    } else {
      toggleAlert('info', 'No selection', 'Select text in the terminal first');
    }
    termInstanceRef.current?.focus();
  }, [toggleAlert]);

  /**
   * Paste from clipboard into terminal (sends as input to backend)
   */
  const handlePaste = useCallback(async () => {
    if (!isConnectedRef.current || !websocketRef.current || !sessionIdRef.current) {
      toggleAlert('warning', 'Not connected', 'Connect the terminal first to paste');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        toggleAlert('info', 'Empty clipboard', 'Nothing to paste');
        return;
      }
      websocketRef.current.send(JSON.stringify({
        type: 'terminal_input',
        session_id: sessionIdRef.current,
        input: text
      }));
      termInstanceRef.current?.focus();
    } catch (err) {
      toggleAlert('error', 'Paste failed', 'Clipboard access denied or unavailable');
    }
  }, [toggleAlert]);

  return (
    <div 
      className={`pod-terminal-fixed ${className}`}
      style={{ 
        display: visible ? 'flex' : 'none',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: `${terminalHeight}vh`,
        zIndex: 1020,
        flexDirection: 'column'
      }}
    >
      {/* Resize Handle */}
      <div 
        className="terminal-resize-handle"
        onMouseDown={handleResizeStart}
        style={{
          height: '8px',
          background: isResizing ? '#1890ff' : '#d9d9d9',
          cursor: 'ns-resize',
          flexShrink: 0,
          position: 'relative',
          borderTop: '1px solid #e8e8e8'
        }}
      >
        <div style={{
          position: 'absolute',
          top: '2px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60px',
          height: '4px',
          background: isResizing ? '#1890ff' : '#bfbfbf',
          borderRadius: '2px'
        }} />
      </div>

      {/* Terminal Header */}
      <div className="terminal-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            {terminalType === 'kubectl' 
              ? `Kubectl Terminal - ${displayClusterName}`
              : `Pod Terminal - ${pod?.name || 'Unknown'}`
            }
          </span>
          <Space>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {terminalType === 'kubectl'
                ? `Namespace: ${pod?.namespace || 'default'} (kubectl context)`
                : `${pod?.namespace || 'Unknown'} / ${selectedContainer || 'No container'}`
              }
            </span>
            <Button 
              type="text" 
              size="small" 
              onClick={handleClose}
              style={{ color: '#666' }}
            >
              ✕
            </Button>
          </Space>
        </div>
      </div>
      <div className="pod-terminal-container">
        {/* Terminal Controls */}
        <div className="terminal-controls">
          <Space size="middle">
            {/* Hide container selection for kubectl sessions */}
            {terminalType !== 'kubectl' && (
              <Select
                value={selectedContainer}
                onChange={handleContainerChange}
                placeholder="Select Container"
                style={{ width: 200 }}
                disabled={isConnected}
              >
                {containers.map((container, index) => (
                  <Option key={index} value={container.name}>
                    {container.name}
                  </Option>
                ))}
              </Select>
            )}

            {!isConnected ? (
              <Button
                type="primary"
                icon={<CodeOutlined />}
                onClick={connectTerminal}
                loading={isConnecting}
                disabled={terminalType !== 'kubectl' && !selectedContainer}
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
            ) : (
              <Button
                danger
                icon={<DisconnectOutlined />}
                onClick={disconnectTerminal}
              >
                Disconnect
              </Button>
            )}

            {/* Copy / Paste / Copy on select */}
            <Space size="small">
              <Tooltip title="Copy selection to clipboard">
                <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>
                  Copy
                </Button>
              </Tooltip>
              <Tooltip title="Paste from clipboard">
                <Button size="small" icon={<SnippetsOutlined />} onClick={handlePaste}>
                  Paste
                </Button>
              </Tooltip>
              <Space size={4} style={{ marginLeft: '8px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Copy on select</span>
                <Switch
                  size="small"
                  checked={copyOnSelect}
                  onChange={setCopyOnSelect}
                  title="When on, selecting text in the terminal copies it to the clipboard"
                />
              </Space>
            </Space>
          </Space>

          {/* Common Commands */}
          {isConnected && (
            <div className="common-commands">
              <span style={{ marginRight: '8px', color: '#666' }}>Common commands:</span>
              {commonCommands.map((cmd, index) => (
                <Button
                  key={index}
                  size="small"
                  onClick={() => executeCommand(cmd.command)}
                  style={{ marginRight: '4px' }}
                >
                  {cmd.label}
                </Button>
              ))}
            </div>
          )}

          {/* Connection Status */}
          <div className="connection-status">
            <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </div>

          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="terminal-error">
            <span style={{ color: '#ff4d4f' }}>Error: {error}</span>
            <Button size="small" onClick={() => setError(null)} style={{ marginLeft: '8px' }}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Terminal Display */}
        <div className="terminal-display">
          {isLoading ? (
            <div className="terminal-loading">
              <Spin size="large" />
              <p>Initializing terminal...</p>
            </div>
          ) : (
            <div 
              ref={terminalRef} 
              className="terminal-element"
              onClick={() => termInstanceRef.current?.focus()}
            />
          )}
        </div>

        {/* Terminal Footer */}
        <div className="terminal-footer">
          <span style={{ fontSize: '12px', color: '#666' }}>
            {terminalType === 'kubectl' 
              ? 'Kubectl terminal with cluster access • Session auto-expires after 5 minutes of inactivity • Use Connect button to reconnect if needed'
              : 'Use Ctrl+C to interrupt commands • Ctrl+D to exit shell • Use Connect button to reconnect if needed'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default PodTerminal;
